import {
  useDeleteShift,
  useShifts,
  useUpdateShift,
} from '@/features/reservation-setup/hooks/useReservationSetup';
import { ChipInput } from '@/components/ChipInput';
import { InfoTip } from '@/components/InfoTip';
import SettingsSaveNav from '@/components/SettingsSaveNav';
import { useT, useErrorT } from '@/i18n';
import { apiErrorText } from '@/api/errors';
import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  List,
  ListButton,
  ListInput,
  ListItem,
  Navbar,
  NavLeft,
  NavTitle,
  Link as F7Link,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { useEffect, useMemo, useState } from 'react';
import type { ReservationShift } from '@restaurant/api-client';

// JS day order Mon..Sun; labels come from the active locale (rs.dayNamesShort /
// rs.dayNamesLong, both indexed by JS day 0=Sun..6=Sat).
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface BaseDraft {
  label: string;
  startTime: string;
  endTime: string;
  daysOfWeek: number[];
}
type DraftState =
  | (BaseDraft & {
      mode: 'SIMPLE';
      maxReservationsPerSlot: string;
      maxCoversPerSlot: string;
    })
  | (BaseDraft & {
      mode: 'ADVANCED';
      pacingMaxCovers: string;
      pacingMaxParties: string;
      maxConcurrentParties: string;
      maxConcurrentCovers: string;
      // Preserved as-is; no UI yet for editing the per-party-size buckets.
      partySizePacing: { minPartySize: number; maxPartiesPerBucket: number }[];
    });

/** Synchronously derive the editable form state from a WINDOW shift, or `null`
 *  if the shift isn't loaded yet / isn't a WINDOW. Used as the `useState` lazy
 *  initializer so the first render already has real form values - critical
 *  for the F7 push transition: returning `null` from the first render leaves
 *  F7 nothing to animate, and the page "pops in" without sliding. */
function deriveDraft(shift: ReservationShift | undefined): DraftState | null {
  if (!shift || shift.scope !== 'WINDOW') return null;
  const base: BaseDraft = {
    label: shift.label,
    startTime: shift.startTime,
    endTime: shift.endTime,
    daysOfWeek: shift.daysOfWeek,
  };
  if (shift.rules.mode === 'SIMPLE') {
    return {
      ...base,
      mode: 'SIMPLE',
      maxReservationsPerSlot:
        shift.rules.maxReservationsPerSlot != null
          ? String(shift.rules.maxReservationsPerSlot)
          : '',
      maxCoversPerSlot:
        shift.rules.maxCoversPerSlot != null ? String(shift.rules.maxCoversPerSlot) : '',
    };
  }
  return {
    ...base,
    mode: 'ADVANCED',
    pacingMaxCovers: shift.rules.pacingMaxCovers != null ? String(shift.rules.pacingMaxCovers) : '',
    pacingMaxParties:
      shift.rules.pacingMaxParties != null ? String(shift.rules.pacingMaxParties) : '',
    maxConcurrentParties:
      shift.rules.maxConcurrentParties != null ? String(shift.rules.maxConcurrentParties) : '',
    maxConcurrentCovers:
      shift.rules.maxConcurrentCovers != null ? String(shift.rules.maxConcurrentCovers) : '',
    partySizePacing: shift.rules.partySizePacing ?? [],
  };
}

/** Parse "" → null, "12" → 12, otherwise NaN (caller treats as invalid). */
function parseCap(v: string): number | null | typeof NaN {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n > 0 ? n : NaN;
}

export default function ShiftDetailPage({
  f7route,
  f7router,
}: {
  f7route: { params: { shiftId: string } };
  f7router: {
    navigate: (url: string) => void;
    back: () => void;
  };
}) {
  const t = useT();
  const errorMessages = useErrorT();
  const rs = t.reservationSetup;
  const { shiftId } = f7route.params;
  const { data: shifts } = useShifts();
  const updateShift = useUpdateShift();
  const deleteShift = useDeleteShift();

  // The shift was pushed on top of the shifts list, so Back just pops the stack.
  const goBack = () => f7router.back();

  const shift = shifts?.find((s) => s.id === shiftId);

  // Derive on mount so the first render is already the populated form -
  // returning `null` (or empty page) from render-1 makes F7's push transition
  // animate against nothing, which reads as "page replaced, no slide".
  const [draft, setDraft] = useState<DraftState | null>(() => deriveDraft(shift));

  // The pristine draft (what's currently in the DB) - used to detect changes.
  const pristine = useMemo(() => deriveDraft(shift), [shift]);
  const isDirty = !!draft && !!pristine && JSON.stringify(draft) !== JSON.stringify(pristine);

  // Cache-miss path only: if the shift wasn't loaded on first render,
  // initialize draft once it arrives.
  useEffect(() => {
    if (shift && !draft) setDraft(deriveDraft(shift));
  }, [shift, draft]);

  if (!shifts) {
    return (
      <Page name="shift-detail">
        <Navbar title={rs.shift}>
          <NavLeft>
            <F7Link onClick={goBack} iconF7="chevron_left" />
          </NavLeft>
        </Navbar>
        <Block className="text-align-center">
          <Preloader />
        </Block>
      </Page>
    );
  }

  if (!shift) {
    return (
      <Page name="shift-detail">
        <Navbar title={rs.shift}>
          <NavLeft>
            <F7Link onClick={goBack} iconF7="chevron_left" />
          </NavLeft>
        </Navbar>
        <Block strongIos strongMd insetIos insetMd className="text-align-center text-color-red">
          {rs.shiftNotFound}
        </Block>
      </Page>
    );
  }

  if (shift.scope !== 'WINDOW') {
    // The baseline is edited inline on the Capacity page, not via this push detail.
    return (
      <Page name="shift-detail">
        <Navbar title={rs.shift}>
          <NavLeft>
            <F7Link onClick={goBack} iconF7="chevron_left" />
          </NavLeft>
        </Navbar>
        <Block strongIos strongMd insetIos insetMd>
          {rs.baselineEditedInline}
        </Block>
      </Page>
    );
  }

  if (!draft) {
    return (
      <Page name="shift-detail">
        <Navbar title={rs.shift}>
          <NavLeft>
            <F7Link onClick={goBack} iconF7="chevron_left" />
          </NavLeft>
        </Navbar>
        <Block className="text-align-center">
          <Preloader />
        </Block>
      </Page>
    );
  }

  const toggleDay = (dow: number) => {
    setDraft((d) =>
      d
        ? {
            ...d,
            daysOfWeek: d.daysOfWeek.includes(dow)
              ? d.daysOfWeek.filter((x) => x !== dow)
              : [...d.daysOfWeek, dow].sort((a, b) => a - b),
          }
        : d,
    );
  };

  const save = () => {
    const label = draft.label.trim();
    if (!label) return f7.dialog.alert(rs.nameRequired);
    if (!/^\d{2}:\d{2}$/.test(draft.startTime) || !/^\d{2}:\d{2}$/.test(draft.endTime)) {
      return f7.dialog.alert(rs.timesMustBeHHmm);
    }
    if (draft.startTime >= draft.endTime) return f7.dialog.alert(rs.startBeforeEnd);
    if (draft.daysOfWeek.length === 0) return f7.dialog.alert(rs.pickAtLeastOneDay);

    // Build mode-specific rules. Shift mode is immutable server-side, so the
    // mode here always matches the draft (and therefore the shift) - never the
    // active capacity mode of the page.
    let rules;
    if (draft.mode === 'SIMPLE') {
      const maxRes = parseCap(draft.maxReservationsPerSlot);
      const maxCovers = parseCap(draft.maxCoversPerSlot);
      if (Number.isNaN(maxRes) || Number.isNaN(maxCovers)) {
        return f7.dialog.alert(rs.capsPositiveWhole);
      }
      rules = {
        mode: 'SIMPLE' as const,
        slotMinutes: 60,
        maxReservationsPerSlot: maxRes as number | null,
        maxCoversPerSlot: maxCovers as number | null,
      };
    } else {
      const pCovers = parseCap(draft.pacingMaxCovers);
      const pParties = parseCap(draft.pacingMaxParties);
      const cParties = parseCap(draft.maxConcurrentParties);
      const cCovers = parseCap(draft.maxConcurrentCovers);
      if (
        Number.isNaN(pCovers) ||
        Number.isNaN(pParties) ||
        Number.isNaN(cParties) ||
        Number.isNaN(cCovers)
      ) {
        return f7.dialog.alert(rs.capsPositiveWhole);
      }
      rules = {
        mode: 'ADVANCED' as const,
        pacingMaxCovers: pCovers as number | null,
        pacingMaxParties: pParties as number | null,
        maxConcurrentParties: cParties as number | null,
        maxConcurrentCovers: cCovers as number | null,
        partySizePacing: draft.partySizePacing,
      };
    }

    updateShift.mutate(
      {
        shiftId: shift.id,
        body: {
          label,
          startTime: draft.startTime,
          endTime: draft.endTime,
          daysOfWeek: draft.daysOfWeek,
          rules,
        },
      },
      {
        // Confirm, then return to the capacity overview — mirrors the create flow,
        // which also lands the user back on the overview after saving.
        onSuccess: () => {
          f7.toast.show({ text: rs.shiftSaved, closeTimeout: 1500 });
          goBack();
        },
        onError: (e) => f7.dialog.alert(apiErrorText(e, errorMessages, rs.failedSaveShift)),
      },
    );
  };

  const remove = () => {
    f7.dialog.confirm(rs.deleteShiftConfirm(shift.label), rs.deleteShift, () => {
      deleteShift.mutate(
        { shiftId: shift.id },
        {
          onSuccess: () => goBack(),
          onError: (e) => f7.dialog.alert(e instanceof Error ? e.message : rs.failedDeleteShift),
        },
      );
    });
  };

  return (
    <Page name="shift-detail">
      <Navbar>
        <NavLeft>
          <F7Link onClick={goBack} iconF7="chevron_left" />
        </NavLeft>
        <NavTitle>{rs.timeSpecificShiftTitle}</NavTitle>
        <SettingsSaveNav isDirty={isDirty} saving={updateShift.isPending} onSave={save} />
      </Navbar>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListInput
          label={rs.name}
          type="text"
          placeholder={rs.lunchDinner}
          value={draft.label}
          onInput={(e) =>
            setDraft((d) => (d ? { ...d, label: (e.target as HTMLInputElement).value } : d))
          }
        />
        <ListItem noChevron title={rs.startTime}>
          <ChipInput
            slot="after"
            type="time"
            value={draft.startTime}
            onChange={(v) => setDraft((d) => (d ? { ...d, startTime: v } : d))}
          />
        </ListItem>
        <ListItem noChevron title={rs.endTime}>
          <ChipInput
            slot="after"
            type="time"
            value={draft.endTime}
            onChange={(v) => setDraft((d) => (d ? { ...d, endTime: v } : d))}
          />
        </ListItem>
      </List>

      <BlockTitle>{rs.daysActive}</BlockTitle>
      <Block strongIos strongMd insetIos insetMd>
        {/* Multi-select day chips: a plain flex row of small round buttons,
            each independently togglable. Segmented is single-select by design
            (iOS segment control), which is why a multi-state version fights
            its CSS / click handling. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DAY_ORDER.map((value) => {
            const on = draft.daysOfWeek.includes(value);
            return (
              <Button
                key={value}
                small
                round
                fill={on}
                outline={!on}
                onClick={() => toggleDay(value)}
                aria-pressed={on}
                aria-label={rs.dayNamesLong[value]}
                style={{ minWidth: 44 }}
              >
                {rs.dayNamesShort[value]}
              </Button>
            );
          })}
        </div>
      </Block>

      {draft.mode === 'SIMPLE' ? (
        <>
          <BlockTitle large>{rs.capacityAndLimit}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{rs.leaveBlankStricter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.maxReservationsSlotLabel}
              type="number"
              placeholder={rs.noLimitation}
              value={draft.maxReservationsPerSlot}
              onInput={(e) =>
                setDraft((d) =>
                  d && d.mode === 'SIMPLE'
                    ? { ...d, maxReservationsPerSlot: (e.target as HTMLInputElement).value }
                    : d,
                )
              }
            />
            <ListInput
              label={rs.maxCoversSlotLabel}
              type="number"
              placeholder={rs.noLimitation}
              value={draft.maxCoversPerSlot}
              onInput={(e) =>
                setDraft((d) =>
                  d && d.mode === 'SIMPLE'
                    ? { ...d, maxCoversPerSlot: (e.target as HTMLInputElement).value }
                    : d,
                )
              }
            />
          </List>
        </>
      ) : (
        <>
          <BlockTitle>
            <span style={{ display: 'flex', alignItems: 'baseline', width: '100%' }}>
              <span style={{ flex: 1 }}>{rs.pacingSimple}</span>
              <InfoTip text={rs.pacingInfo} />
            </span>
          </BlockTitle>
          <BlockFooter>
            <span style={{ color: '#ff3b30', fontWeight: 600 }}>⚠ {rs.detailOverrideWarning}</span>
          </BlockFooter>
          <BlockFooter style={{ marginTop: -6 }}>{rs.leaveBlankStricter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.maxCovers}
              type="number"
              placeholder={rs.noLimitation}
              value={draft.pacingMaxCovers}
              onInput={(e) =>
                setDraft((d) =>
                  d && d.mode === 'ADVANCED'
                    ? { ...d, pacingMaxCovers: (e.target as HTMLInputElement).value }
                    : d,
                )
              }
            />
            <ListInput
              label={rs.maxParties}
              type="number"
              placeholder={rs.noLimitation}
              value={draft.pacingMaxParties}
              onInput={(e) =>
                setDraft((d) =>
                  d && d.mode === 'ADVANCED'
                    ? { ...d, pacingMaxParties: (e.target as HTMLInputElement).value }
                    : d,
                )
              }
            />
          </List>

          <BlockTitle>
            <span style={{ display: 'flex', alignItems: 'baseline', width: '100%' }}>
              <span style={{ flex: 1 }}>{rs.concurrentSimple}</span>
              <InfoTip text={rs.concurrentInfo} />
            </span>
          </BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>
            {`${rs.concurrentFooter} ${rs.partySizePacing}`}
          </BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={rs.maxParties}
              type="number"
              placeholder={rs.noLimitation}
              value={draft.maxConcurrentParties}
              onInput={(e) =>
                setDraft((d) =>
                  d && d.mode === 'ADVANCED'
                    ? { ...d, maxConcurrentParties: (e.target as HTMLInputElement).value }
                    : d,
                )
              }
            />
            <ListInput
              label={rs.maxCovers}
              type="number"
              placeholder={rs.noLimitation}
              value={draft.maxConcurrentCovers}
              onInput={(e) =>
                setDraft((d) =>
                  d && d.mode === 'ADVANCED'
                    ? { ...d, maxConcurrentCovers: (e.target as HTMLInputElement).value }
                    : d,
                )
              }
            />
          </List>
        </>
      )}

      <List strongIos strongMd insetIos insetMd>
        <ListButton color="red" onClick={remove}>
          {rs.deleteShift}
        </ListButton>
      </List>
    </Page>
  );
}
